import { useEffect, useRef, useState } from "react";
import { FileText, Upload, Trash2, Download, CheckCircle2, XCircle, X, Lock, Users, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABELS = {
  contract: { de: "Vertrag", fr: "Contrat", it: "Contratto", en: "Contract" },
  proof: { de: "Nachweis", fr: "Justificatif", it: "Prova", en: "Proof" },
  certificate: { de: "Zertifikat", fr: "Certificat", it: "Certificato", en: "Certificate" },
  upload: { de: "Upload", fr: "Fichier", it: "Caricamento", en: "Upload" },
  customer_doc: { de: "Kundenunterlage", fr: "Doc. client", it: "Doc. cliente", en: "Customer doc" },
  provider_doc: { de: "Anbieterunterlage", fr: "Doc. prestataire", it: "Doc. fornitore", en: "Provider doc" },
  internal_admin: { de: "Intern (Admin)", fr: "Interne (admin)", it: "Interno (admin)", en: "Internal (admin)" },
  review_approval: { de: "Prüf-/Freigabe", fr: "Vérification", it: "Verifica", en: "Review/approval" },
};
const VIS_LABELS = {
  private: { de: "Privat", fr: "Privé", it: "Privato", en: "Private" },
  internal: { de: "Intern", fr: "Interne", it: "Interno", en: "Internal" },
  shared: { de: "Geteilt", fr: "Partagé", it: "Condiviso", en: "Shared" },
};
const STATUS_LABELS = {
  draft: { de: "Entwurf", fr: "Brouillon", it: "Bozza", en: "Draft" },
  active: { de: "Aktiv", fr: "Actif", it: "Attivo", en: "Active" },
  pending_review: { de: "Zur Prüfung", fr: "À vérifier", it: "In verifica", en: "Pending review" },
  approved: { de: "Freigegeben", fr: "Approuvé", it: "Approvato", en: "Approved" },
  rejected: { de: "Abgelehnt", fr: "Rejeté", it: "Rifiutato", en: "Rejected" },
  expired: { de: "Abgelaufen", fr: "Expiré", it: "Scaduto", en: "Expired" },
  archived: { de: "Archiviert", fr: "Archivé", it: "Archiviato", en: "Archived" },
};
const STATUS_COLORS = {
  approved: { bg: "#DCFCE7", fg: "#15803D" }, active: { bg: "#DCFCE7", fg: "#15803D" },
  pending_review: { bg: "#FEF3C7", fg: "#B45309" }, draft: { bg: "#E5E7EB", fg: "#374151" },
  rejected: { bg: "#FEE2E2", fg: "#B91C1C" }, expired: { bg: "#FEE2E2", fg: "#B91C1C" },
  archived: { bg: "#E5E7EB", fg: "#374151" },
};
const VIS_ICON = { private: Lock, internal: Eye, shared: Users };

function fmtSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

const emptyForm = { title: "", document_type: "upload", description: "", visibility: "private", status: "active", expires_at: "", owner_type: "portal" };

export function DocumentsManager() {
  const { L, lang } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [m, d] = await Promise.all([api.get("/documents/meta"), api.get("/documents")]);
      setMeta(m.data);
      setItems(d.data);
    } catch (e) {
      setItems([]);
      setMeta((x) => x || { types: [], statuses: [], visibilities: [], is_manager: false });
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const manager = meta?.is_manager;
  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };

  const typeOptions = (meta?.types || []).filter((t) => manager || t !== "internal_admin");
  const visOptions = (meta?.visibilities || []).filter((v) => manager || v !== "internal");

  const submit = async () => {
    if (!file) { toast.error(L({ de: "Bitte Datei wählen", fr: "Choisir un fichier", it: "Scegli un file", en: "Choose a file" })); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", form.title || file.name);
      fd.append("document_type", form.document_type);
      fd.append("description", form.description || "");
      fd.append("visibility", form.visibility);
      if (manager) { fd.append("status", form.status); fd.append("owner_type", form.owner_type); }
      if (form.expires_at) fd.append("expires_at", form.expires_at);
      await api.post("/documents/upload", fd);
      toast.success(L({ de: "Dokument hochgeladen", fr: "Document téléversé", it: "Documento caricato", en: "Document uploaded" }));
      setOpen(false); setForm(emptyForm); setFile(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || doc.title; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (doc, status) => {
    try { await api.patch(`/documents/${doc.id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (doc) => {
    if (!window.confirm(L({ de: `Dokument „${doc.title}" wirklich löschen?`, fr: `Supprimer le document « ${doc.title} » ?`, it: `Eliminare il documento «${doc.title}»?`, en: `Delete document "${doc.title}"?` }))) return;
    try { await api.delete(`/documents/${doc.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!items || !meta) return <div className="py-16 text-center text-sm" style={{ color: "var(--text-muted)" }}>…</div>;

  return (
    <div className="space-y-4" data-testid="documents-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {items.length} · {L({ de: "Dokumente", fr: "Documents", it: "Documenti", en: "Documents" })}
        </p>
        <button
          data-testid="documents-upload-btn"
          onClick={() => { setForm(emptyForm); setFile(null); setOpen(true); }}
          className="inline-flex items-center gap-2 px-4 h-10 text-sm font-medium"
          style={{ background: "var(--primary)", color: "var(--primary-fg)", borderRadius: "var(--radius)" }}
        >
          <Upload className="h-4 w-4" /> {L({ de: "Hochladen", fr: "Téléverser", it: "Carica", en: "Upload" })}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed rounded-xl py-16 text-center" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }} data-testid="documents-empty">
          <FileText className="mx-auto h-8 w-8 opacity-50" />
          <p className="mt-3 text-sm">{L({ de: "Noch keine Dokumente", fr: "Aucun document", it: "Nessun documento", en: "No documents yet" })}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((d) => {
            const sc = STATUS_COLORS[d.status] || { bg: "var(--surface-alt)", fg: "var(--text-muted)" };
            const VIco = VIS_ICON[d.visibility] || Lock;
            const canDelete = manager || d.uploaded_by === user?.id;
            return (
              <div key={d.id} data-testid={`document-row-${d.id}`}
                className="flex flex-col sm:flex-row sm:items-center gap-3 border p-4 rounded-xl"
                style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg shrink-0" style={{ background: "var(--surface-alt)", color: "var(--accent)" }}>
                  <FileText className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold truncate" style={{ color: "var(--text)" }}>{d.title}</span>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>{L(TYPE_LABELS[d.document_type]) || d.document_type}</span>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}><VIco className="h-3 w-3" />{L(VIS_LABELS[d.visibility]) || d.visibility}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.fg }}>{L(STATUS_LABELS[d.status]) || d.status}</span>
                  </div>
                  <div className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
                    {fmtSize(d.file_size)} · {d.uploaded_by_name || "—"} · {new Date(d.created_at).toLocaleDateString(lang)}
                    {d.expires_at && ` · ${L({ de: "läuft ab", fr: "expire", it: "scade", en: "expires" })} ${new Date(d.expires_at).toLocaleDateString(lang)}`}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`document-download-${d.id}`} onClick={() => download(d)} title="Download"
                    className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-medium border rounded-lg" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  {manager && d.status === "pending_review" && (
                    <>
                      <button data-testid={`document-approve-${d.id}`} onClick={() => setStatus(d, "approved")} className="inline-flex items-center px-2.5 h-9 rounded-lg" style={{ background: "#DCFCE7", color: "#15803D" }}><CheckCircle2 className="h-4 w-4" /></button>
                      <button data-testid={`document-reject-${d.id}`} onClick={() => setStatus(d, "rejected")} className="inline-flex items-center px-2.5 h-9 rounded-lg" style={{ background: "#FEE2E2", color: "#B91C1C" }}><XCircle className="h-4 w-4" /></button>
                    </>
                  )}
                  {canDelete && (
                    <button data-testid={`document-delete-${d.id}`} onClick={() => remove(d)} className="inline-flex items-center px-2.5 h-9 rounded-lg" style={{ background: "var(--surface-alt)", color: "#dc2626" }}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.5)" }} data-testid="documents-modal">
          <div className="w-full max-w-lg p-6 max-h-[90vh] overflow-auto border" style={{ background: "var(--surface)", borderColor: "var(--border)", borderRadius: "var(--radius)" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Upload className="h-5 w-5" style={{ color: "var(--accent)" }} /> {L({ de: "Dokument hochladen", fr: "Téléverser un document", it: "Carica documento", en: "Upload document" })}</h3>
              <button onClick={() => setOpen(false)} data-testid="documents-modal-close"><X className="h-5 w-5" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{L({ de: "Datei", fr: "Fichier", it: "File", en: "File" })}</label>
                <input ref={fileRef} data-testid="documents-file-input" type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  onChange={(e) => { const f = e.target.files?.[0]; setFile(f); if (f && !form.title) setForm((x) => ({ ...x, title: f.name.replace(/\.[^.]+$/, "") })); }}
                  className="w-full px-3.5 py-2.5 text-sm border outline-none" style={st} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{L({ de: "Titel", fr: "Titre", it: "Titolo", en: "Title" })}</label>
                <input data-testid="documents-title-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{L({ de: "Typ", fr: "Type", it: "Tipo", en: "Type" })}</label>
                  <select data-testid="documents-type-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })}>
                    {typeOptions.map((t) => <option key={t} value={t}>{L(TYPE_LABELS[t]) || t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{L({ de: "Sichtbarkeit", fr: "Visibilité", it: "Visibilità", en: "Visibility" })}</label>
                  <select data-testid="documents-visibility-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}>
                    {visOptions.map((v) => <option key={v} value={v}>{L(VIS_LABELS[v]) || v}</option>)}
                  </select>
                </div>
              </div>
              {manager && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{L({ de: "Status", fr: "Statut", it: "Stato", en: "Status" })}</label>
                    <select data-testid="documents-status-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                      value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      {(meta?.statuses || []).map((s) => <option key={s} value={s}>{L(STATUS_LABELS[s]) || s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{L({ de: "Ablauf", fr: "Expiration", it: "Scadenza", en: "Expires" })}</label>
                    <input data-testid="documents-expires-input" type="date" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                      value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{L({ de: "Beschreibung", fr: "Description", it: "Descrizione", en: "Description" })}</label>
                <textarea data-testid="documents-description-input" rows={2} className="w-full px-3.5 py-2.5 text-sm border outline-none" style={st}
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <button data-testid="documents-submit-btn" onClick={submit} disabled={uploading || !file}
                className="w-full inline-flex items-center justify-center gap-2 h-11 text-sm font-medium disabled:opacity-50"
                style={{ background: "var(--primary)", color: "var(--primary-fg)", borderRadius: "var(--radius)" }}>
                {uploading ? "…" : L({ de: "Hochladen", fr: "Téléverser", it: "Carica", en: "Upload" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
